import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { InputSelect } from "components/Form/InputSelect";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import { getRole } from "hooks";
import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeCollectPoint, updateCollectPoint } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  collectPointId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  pointRewardCollected: "",
  pointXpCollected: "",
};

const CollectPointFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  collectPointId,
}) => {
  const isAddMode = !collectPointId;
  const dispatch = useDispatch();
  const userRole = getRole();
  const formikRef = useRef<FormikValues>() as any;

  const [tenant, setTenant] = useState<ValueSchema | null>(null);

  const { isFormLoading, collectPoints } = useSelector(
    (state: any) => state.collectPointReducer
  );

  const [tenantItems] = React.useState([
   { label: "Ancol Points", value: 3 },
  ]);

  const handleCloseModal = () => {
    onClose && onClose();
    setTenant(null);
  };

  const handleSubmit = (values: any) => {
    const { pointRewardCollected, pointXpCollected, tenantId } = values;

    isAddMode
      ? dispatch(
          storeCollectPoint(
            { pointRewardCollected, pointXpCollected, tenantId },
            handleCloseModal
          ) as any
        )
      : dispatch(
          updateCollectPoint(
            collectPointId,
            { pointRewardCollected, pointXpCollected, tenantId },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const collectPointDetail = collectPoints.find(
        (point: any) => point.id === parseInt(collectPointId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue(
          "pointRewardCollected",
          collectPointDetail.point_reward_collected
        );
        formikRef.current.setFieldValue(
          "pointXpCollected",
          collectPointDetail.point_xp_collected
        );
        formikRef.current.setFieldValue(
          "tenantId",
          collectPointDetail.tenant_id
        );

        tenantItems.map((item) =>
          item.value.toString() === collectPointDetail.tenant_id
            ? setTenant(item)
            : null
        );
      }
    }
  }, [isAddMode, collectPoints, collectPointId]);

  const validationSchema = yup.object({
    pointRewardCollected: yup
      .number()
      .required("Point Reward is required field"),
    pointXpCollected: yup.number().required("Point XP is required field"),
    tenantId:
      userRole === "superadmin"
        ? yup.string().required("Tenant is required field")
        : yup.string(),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode
                    ? "Add Collection Point"
                    : "Update Collection Point"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="pointRewardCollected">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="pointRewardCollected">
                          Point Reward Collected
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="number"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="pointXpCollected">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="pointXpCollected">
                          Point XP Collected
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="number"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  {userRole === "superadmin" ? (
                    <Field name="tenantId">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="tenantId">Tenant</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={tenant}
                              width={"100%"}
                              placeholder="Select Tenant"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setTenant(selectedItem);
                                  setFieldValue("tenantId", selectedItem.value);
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={tenantItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  ) : (
                    <></>
                  )}
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CollectPointFormModal;
